import React, { useContext, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import useReactHookFormStyles from '../../../../custom-components/form-components/ReactHookForm.styles';

import { fetchOptions } from '../queries';
import LocalizationContext from '../../../../localization/LocalizationContext';

const Select = ({ control, name, fieldName, componentName, field, filterRef, query, setHasOptions, uniqueValues = true, props: { label, labelKey, options: configOptions, variant, required, shouldUnregister, noOptionsText, ...rest } }) => {
    const {
        clearErrors,
        formState: { errors },
        getValues,
        setError,
        updateSelectedValues,
        selectedValues,
        watch,
    } = useFormContext();
    const classes = useReactHookFormStyles();
    const localization = useContext(LocalizationContext);
    const resolvedNoOptionsText = noOptionsText?.replace('{processor}', localization?.general?.processor_singular ?? 'Plant');
    const [options, setOptions] = useState(configOptions);
    const [filteredOptions, setFilteredOptions] = useState(configOptions);
    const [loading, setLoading] = useState(false);

    const { endpoint, where = {}, fields, order, roleConstraints, populate, useLoopback } = query || {};
    const { referenceField, filterKey } = filterRef || {};

    const getCurrentValue = () => {
        const current = getValues(name);
        return current === undefined ? field?.[componentName] : current;
    };

    const filterAndSetOptions = (optionList) => {
        // Fetch all corresponding selects for this field array
        const existingSelects = selectedValues[fieldName]?.map((row) => row[componentName]);
        const currentId = getCurrentValue();
        // If the option is not selected selected or is currently selected, keep the option
        // Filter the options that have already been selected
        const filtered = optionList?.filter((option) => {
            const optionId = option.id ?? option._id;
            return !existingSelects?.includes(optionId) || optionId === currentId;
        });
        if (filtered) setFilteredOptions(filtered);
    };

    useEffect(
        () => {
            const value = getValues(referenceField);

            let cancelled = false;

            const withSavedValue = async (optionList) => {
                const raw = getCurrentValue();
                const savedId = raw?._id ?? raw?.id ?? raw;
                if (!savedId || optionList.some((option) => (option.id ?? option._id) === savedId)) return optionList;
                const saved = await fetchOptions({ endpoint, where: { _id: savedId, [filterKey]: value }, fields, populate, useLoopback });
                return Array.isArray(saved) && saved.length ? [...optionList, ...saved] : optionList;
            };

            const getOptions = async () => {
                setLoading(true);
                try {
                    const whereClause = { ...where };
                    // extend where object for filtering options
                    whereClause[filterKey] = value;
                    const optionsResponse = await fetchOptions({ endpoint, where: whereClause, fields, order, roleConstraints, populate, useLoopback });
                    if (cancelled) return;

                    if (optionsResponse.error) {
                        setHasOptions(false);
                        setOptions([]);
                        setFilteredOptions([]);
                        setError(name, { type: 'fetch options', message: `Could not fetch ${label} options` });
                    } else {
                        if (optionsResponse.length) setHasOptions(true);
                        const allOptions = await withSavedValue(optionsResponse);
                        if (cancelled) return;
                        // set original options list into state
                        setOptions(allOptions);
                        // filter and set options
                        filterAndSetOptions(allOptions);
                        clearErrors([name]);
                    }
                } finally {
                    if (!cancelled) setLoading(false);
                }
            };

            if (!uniqueValues) return undefined;
            if (value) {
                getOptions();
            } else {
                setHasOptions(false);
                setOptions([]);
                setFilteredOptions([]);
                setLoading(false);
            }
            return () => {
                cancelled = true;
            };
        },
        !!referenceField ? [watch(referenceField)] : []
    );

    // Relying on selectedValues to trigger filtering
    // useFormContext().watch() does not trigger when these nested field array values change
    useEffect(() => {
        if (uniqueValues) {
            // filter and set options when values change
            filterAndSetOptions(options);
        }
    }, [selectedValues]);

    const getOptionLabel = (option) => {
        if (!option) {
            return '';
        }
        const key = labelKey || 'name';
        if (option[key]) {
            return option[key];
        }
        const optionId = option?._id ?? option?.id ?? option;
        const foundOption = filteredOptions?.find((opt) => (opt.id || opt._id) === optionId);
        return foundOption?.[key] || '';
    };

    const renderInput = (props) => (
        <TextField
            {...props}
            label={label}
            required={required}
            variant={variant || 'outlined'}
            error={errors[name]}
            helperText={errors[name]?.message}
            // wrap
        />
    );

    if ((loading || !filteredOptions) && !errors[name]) {
        return (
            <TextField
                value="Loading options"
                disabled
                required={required}
                label={label}
                variant={variant || 'outlined'}
                className={classes.textField}
                // wrap
            />
        );
    }

    return (
        <FormControl className={classes.formController}>
            <Controller
                name={name}
                control={control}
                shouldUnregister={shouldUnregister}
                render={({ field: { onChange, value } }) => {
                    return (
                        <Autocomplete
                            value={value || null}
                            onChange={(e, item) => {
                                onChange(item?.id || item?._id || item || null);
                                const fieldArrayValue = getValues(fieldName);
                                updateSelectedValues(fieldName, fieldArrayValue);
                            }}
                            options={filteredOptions}
                            noOptionsText={resolvedNoOptionsText}
                            renderInput={renderInput}
                            getOptionLabel={getOptionLabel}
                            getOptionSelected={(option, val) => {
                                const valId = val?.id || val?._id || val;
                                const optionId = option?.id || option?._id || option;
                                return valId === optionId;
                            }}
                            {...rest}
                            // wrap
                        />
                    );
                }}
            />
        </FormControl>
    );
};

export default Select;
